import { useTranslation } from 'react-i18next';

import { paths } from 'src/routes/paths';
import { useJsonLd } from 'src/hooks/use-json-ld';
import { Reveal } from 'src/components/reveal';
import { CtaDuo } from 'src/components/cta-duo';
import { PageSeo } from 'src/components/page-seo';
import { NightLog } from 'src/components/night-log';
import { PageHero } from 'src/components/page-hero';
import { CardGrid } from 'src/components/card-grid';
import { Manifesto } from 'src/components/manifesto';
import { SectionHead } from 'src/components/section-head';
import { BenefitGrid } from 'src/components/benefit-grid';
import { NightTimeline } from 'src/components/night-timeline';

import type { ServicePageConfig } from './service-pages';
import { useServiceStructuredData } from './use-service-structured-data';

export function ServiceView({ path, namespace, secondaryCtaPath }: ServicePageConfig) {
  const { t } = useTranslation(namespace);
  const { t: tCommon } = useTranslation('common');
  const structuredData = useServiceStructuredData(namespace, path);

  useJsonLd(...structuredData);

  return (
    <>
      <PageSeo path={path} title={t('meta.title')} description={t('meta.description')} />

      <PageHero
        lead={t('hero.lead')}
        title={t('hero.title')}
        eyebrow={t('hero.eyebrow')}
        current={t('hero.breadcrumb')}
        action={{ to: paths.contact, label: tCommon('actions.talkAboutProject') }}
        links={[
          { to: paths.home, label: tCommon('labels.home') },
          { to: paths.servicesAnchor, label: tCommon('labels.services') },
        ]}
      />

      <section className="section loose-top">
        <div className="container narrow">
          <Reveal>
            <NightLog lines={t('console.lines', { returnObjects: true })} />
          </Reveal>
        </div>
      </section>

      <Manifesto compact ns={namespace} />

      <section className="section flush-top">
        <div className="container">
          <SectionHead title={t('benefits.title')} eyebrow={t('benefits.eyebrow')} />
          <BenefitGrid items={t('benefits.items', { returnObjects: true })} />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead title={t('scope.title')} eyebrow={t('scope.eyebrow')} />
          <CardGrid items={t('scope.items', { returnObjects: true })} />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead title={t('process.title')} eyebrow={t('process.eyebrow')} />
          <NightTimeline steps={t('process.steps', { returnObjects: true })} />
        </div>
      </section>

      <div role="presentation" className="horizon" />

      <section className="section day">
        <div className="container">
          <CtaDuo
            secondary={{
              to: secondaryCtaPath,
              title: t('cta.secondaryTitle'),
              action: t('cta.secondaryAction'),
              label: tCommon('labels.seeAlso'),
            }}
            primary={{
              to: paths.contact,
              title: t('cta.primaryTitle'),
              label: tCommon('labels.firstStep'),
              action: tCommon('actions.writeViaForm'),
            }}
          />
        </div>
      </section>
    </>
  );
}
